import { invariant } from "ts-invariant";
import { z } from "zod";

/**
 * tldraw v5 stopped exporting its font constants (internally they became
 * theme-relative ems on a 16px base). These are the resolved default-theme
 * pixel values, identical to what tldraw 4.x exported as FONT_SIZES.
 */
export const FONT_SIZES: Record<"s" | "m" | "l" | "xl", number> = {
  s: 18,
  m: 24,
  l: 36,
  xl: 44,
};

export const SimpleFontSize = z.number();

/**
 * Calculates the closest predefined font size and scale combination to achieve a target font size
 * @param targetFontSize - The desired font size in pixels
 * @returns An object containing the closest predefined font size key and the scale factor
 */
export function convertSimpleFontSizeToTldrawFontSizeAndScale(
  targetFontSize: number,
) {
  const fontSizeEntries = Object.entries(FONT_SIZES);
  let closestSize = fontSizeEntries[0];
  invariant(closestSize !== undefined, "FONT_SIZES must not be empty");
  let minDifference = Math.abs(targetFontSize - closestSize[1]);

  for (const [size, fontSize] of fontSizeEntries) {
    const difference = Math.abs(targetFontSize - fontSize);
    if (difference < minDifference) {
      minDifference = difference;
      closestSize = [size, fontSize];
    }
  }

  const textSize = closestSize[0] as keyof typeof FONT_SIZES;
  const baseFontSize = closestSize[1];
  const scale = targetFontSize / baseFontSize;

  return { textSize, scale };
}

/**
 * Converts a tldraw font size and scale to a simple font size
 * @param textSize - The tldraw font size
 * @param scale - The tldraw scale
 * @returns The simple font size
 */
export function convertTldrawFontSizeAndScaleToSimpleFontSize(
  textSize: keyof typeof FONT_SIZES,
  scale: number,
) {
  return Math.round(FONT_SIZES[textSize] * scale);
}
